import React, { useState } from 'react';
import { Card, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { Calendar, Clock, Plus, CalendarRange } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import slotService from '../../services/slotService';

const SlotCreator = ({ onSlotCreated }) => {
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    duration: 30, // Default 30 minutes
    interviewer: '',
    meetingLink: '',
    skipWeekends: true
  });
  
  const [loading, setLoading] = useState(false);
  const [previewSlots, setPreviewSlots] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Duration options in minutes
  const durationOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Reset preview when form changes
    if (showPreview) {
      setShowPreview(false);
      setPreviewSlots([]);
    }
  };

  // Generate preview slots based on form data
  const generatePreviewSlots = () => {
    try {
      const { startDate, endDate, startTime, endTime, duration, skipWeekends, interviewer } = formData;
      
      if (!startDate || !endDate || !startTime || !endTime) {
        toast.warning('Please fill in all date and time fields');
        return;
      }

      if (new Date(startDate) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }

      const slots = [];
      const currentDate = new Date(startDate);
      const finalDate = new Date(endDate);
      const now = new Date();

      while (currentDate <= finalDate) {
        // Skip weekends if option is selected
        if (skipWeekends && (currentDate.getDay() === 0 || currentDate.getDay() === 6)) {
          currentDate.setDate(currentDate.getDate() + 1);
          continue;
        }

        // Generate slots for current day
        const daySlots = generateSlotsForDay(currentDate, startTime, endTime, duration);
        
        daySlots.forEach(slot => {
          // Only add slots that are in the future (with 15-minute buffer)
          const slotTime = new Date(slot);
          const bufferTime = new Date(now.getTime() + (15 * 60 * 1000)); // 15 minutes from now
          
          if (slotTime >= bufferTime) {
            slots.push({
              dateTime: slot,
              interviewer: interviewer || 'To be assigned',
              date: currentDate.toDateString()
            });
          }
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      setPreviewSlots(slots);
      setShowPreview(true);

      if (slots.length === 0) {
        toast.warning('No future slots generated. Please check your date range and time settings.');
      } else {
        toast.info(`Generated ${slots.length} slots for preview`);
      }

    } catch (error) {
      toast.error('Error generating slot preview');
    }
  };

  // Generate slots for a specific day
  const generateSlotsForDay = (date, startTime, endTime, durationMinutes) => {
    const slots = [];
    
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startDateTime = new Date(date);
    startDateTime.setHours(startHour, startMinute, 0, 0);
    
    const endDateTime = new Date(date);
    endDateTime.setHours(endHour, endMinute, 0, 0);
    
    if (startDateTime >= endDateTime) {
      return slots; // Invalid time range
    }
    
    const currentSlot = new Date(startDateTime);
    
    while (currentSlot < endDateTime) {
      // Check if there's enough time for a complete slot
      const nextSlot = new Date(currentSlot.getTime() + (durationMinutes * 60000));
      if (nextSlot <= endDateTime) {
        slots.push(new Date(currentSlot));
      }
      currentSlot.setTime(currentSlot.getTime() + (durationMinutes * 60000));
    }
    
    return slots;
  };

  // Create all slots
  const handleCreateSlots = async () => {
    if (previewSlots.length === 0) {
      toast.warning('Please generate preview slots first');
      return;
    }

    if (!formData.interviewer.trim()) {
      toast.error('Interviewer name is required');
      return;
    }

    setLoading(true);

    try {
      const slotsData = previewSlots.map(slot => ({
        slotDateTime: slot.dateTime.toISOString(),
        interviewer: formData.interviewer.trim(),
        meetingLink: formData.meetingLink.trim() || null
      }));

      const result = await slotService.createBulkSlots(slotsData);
      
      if (result.successful > 0) {
        toast.success(
          <div>
            <strong>Slots Created Successfully!</strong>
            <br />
            <small>{result.successful} slots created</small>
            {result.failed > 0 && (
              <>
                <br />
                <small className="text-warning">{result.failed} slots failed (may already exist)</small>
              </>
            )}
          </div>
        );

        // Reset form
        setFormData({
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          duration: 30,
          interviewer: '',
          meetingLink: '',
          skipWeekends: true
        });
        setPreviewSlots([]);
        setShowPreview(false);

        if (onSlotCreated) {
          onSlotCreated();
        }
      } else {
        toast.error('No slots were created successfully');
      }

    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Failed to create slots: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Group preview slots by date for better display
  const groupedPreviewSlots = previewSlots.reduce((groups, slot) => {
    const date = slot.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(slot);
    return groups;
  }, {});

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-success text-white">
        <h5 className="mb-0 d-flex align-items-center">
          <CalendarRange className="me-2" />
          Create Interview Slots
        </h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={(e) => e.preventDefault()}>
          {/* Date Range Selection */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <Calendar className="me-1" />
                  Start Date
                </Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <Calendar className="me-1" />
                  End Date
                </Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate || new Date().toISOString().split('T')[0]}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Time Range Selection */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <Clock className="me-1" />
                  Start Time
                </Form.Label>
                <Form.Control
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <Clock className="me-1" />
                  End Time
                </Form.Label>
                <Form.Control
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Duration</Form.Label>
                <Form.Select
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                >
                  {durationOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Interviewer and Meeting Link */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Interviewer Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="interviewer"
                  placeholder="Enter interviewer name"
                  value={formData.interviewer}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Meeting Link (Optional)</Form.Label>
                <Form.Control
                  type="url"
                  name="meetingLink"
                  placeholder="https://zoom.us/j/... or https://teams.microsoft.com/..."
                  value={formData.meetingLink}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Options */}
          <Row className="mb-3">
            <Col>
              <Form.Check
                type="checkbox"
                name="skipWeekends"
                label="Skip weekends (Saturday & Sunday)"
                checked={formData.skipWeekends}
                onChange={handleChange}
              />
            </Col>
          </Row>

          {/* Action Buttons */}
          <div className="d-flex gap-2 mb-3">
            <Button
              variant="outline-primary"
              onClick={generatePreviewSlots}
              disabled={loading}
            >
              <Calendar className="me-1" />
              Preview Slots
            </Button>
            
            {showPreview && previewSlots.length > 0 && (
              <Button
                variant="success"
                onClick={handleCreateSlots}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="me-1" />
                    Create {previewSlots.length} Slots
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Slot Preview */}
          {showPreview && previewSlots.length > 0 && (
            <Alert variant="info">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Slot Preview ({previewSlots.length} slots)</strong>
                <Badge bg="primary" pill>{previewSlots.length} total</Badge>
              </div>
              
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {Object.entries(groupedPreviewSlots).map(([date, slots]) => (
                  <div key={date} className="mb-2">
                    <div className="fw-semibold text-primary">{date}</div>
                    <div className="ms-3">
                      {slots.map((slot, index) => (
                        <Badge 
                          key={index} 
                          bg="light" 
                          text="dark" 
                          className="me-1 mb-1"
                        >
                          {slot.dateTime.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <small className="text-muted">
                Duration: {formData.duration} minutes each | Interviewer: {formData.interviewer || 'To be assigned'}
              </small>
            </Alert>
          )}

          {/* Usage Help */}
          <Alert variant="light" className="small">
            <strong>How it works:</strong>
            <ol className="mb-0 mt-1">
              <li>Select start and end dates for the slot range</li>
              <li>Set daily time range (e.g., 1:00 PM to 3:00 PM)</li>
              <li>Choose slot duration (e.g., 30 minutes)</li>
              <li>This will create slots: 1:00 PM, 1:30 PM, 2:00 PM, 2:30 PM</li>
              <li>Preview before creating to verify the slots</li>
            </ol>
          </Alert>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default SlotCreator;
